<template>
  <b-modal
    id="mc-modal-detail"
    ref="mc-modal-detail"
    class="mc-modal"
    :title="$t('landMeasurement.field.areaDetail')"
    size="xl"
    centered
    @shown="handleModalShown"
  >
    <div class="card-body">
      <FormulateForm ref="formDetailRef" name="form-detail">
        <b-row>
          <b-col md="6">
            <GmapMap
              id="map"
              ref="MapAddress"
              :center="$store.state.map.center"
              :zoom="parseFloat($store.state.map.zoom)"
              :options="gMapOptions"
              class="mc-map-land"
            />
          </b-col>
          <b-col md="6">
            <ReadOnly :items="readOnlyItems" />
            <b-row>
              <b-col md="12">
                <McButton
                  variant="secondary"
                  class="me-2"
                  size="full"
                  :text="$t('landMeasurement.changeData')"
                  @click="toDetail(item.zoneId)"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </FormulateForm>
    </div>
    <template #modal-footer="{ cancel }">
      <div class="d-flex">
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.action.close')"
          @click="cancel()"
        />
      </div>
    </template>
  </b-modal>
</template>
<script>
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'
import ReadOnly from './components/ReadOnly.vue'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { gmapApi } from 'vue2-google-maps'
import { GET_SUMMARY_TRIP } from '@/services/api/fleet'

moment.locale('id')
export default {
  components: {
    McButton,
    ReadOnly,
  },
  mixins: [Helper],
  props: {
    item: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: {
        poly_data: [],
      },
      totalDistance: null,
      gMapOptions: {
        mapTypeId: 'hybrid',
      },
    }
  },
  computed: {
    google: gmapApi,
    readOnlyItems() {
      const getItemValue = (field) => {
        if (isEmpty(this.item) || !this.item[field]) {
          return '-'
        }

        switch (field) {
          case 'createdOn':
          case 'updatedOn':
            return this.momentDatetimeShortMonth(this.item[field])
          case 'startWorkTime':
          case 'endWorkTime':
            return this.momentTime(this.item[field])
          case 'geometry':
            return this.item[field].substring(1, this.item[field].length - 1)
          case 'landArea':
            return this.thousandFormat(this.item[field])
          default:
            return this.item[field]
        }
      }

      const getVehicleValue = (field) => {
        return !isEmpty(this.item?.vehicle?.[field]) ? this.item.vehicle[field] : '-'
      }

      const getPersonValue = (field, defaultValue = '-') => {
        return !isEmpty(this.item?.[field]) ? this.item[field].fullname : defaultValue
      }

      const items = [
        {
          label: this.$t('landMeasurement.field.landName'),
          value: getItemValue('name'),
          bold: true,
        },
        { label: this.$t('landMeasurement.field.landId'), value: getItemValue('code'), bold: true },
        {
          label: this.$t('landMeasurement.field.description'),
          value: getItemValue('description'),
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.coordinate'),
          value: getItemValue('geometry'),
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.landArea'),
          value: getItemValue('landArea'),
          bold: true,
        },
        {
          label: `${this.$t('landMeasurement.field.licensePlate')} / ${this.$t(
            'landMeasurement.field.chassisNo'
          )}`,
          value: `${getVehicleValue('licensePlate')} / ${getVehicleValue('chassisNumber')}`,
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.workHours'),
          value: `${getItemValue('startWorkTime')} - ${getItemValue('endWorkTime')}`,
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.totalDistance'),
          value: this.thousandFormat(this.totalDistance),
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.createdBy'),
          value: getPersonValue('createdBy'),
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.createdDate'),
          value: getItemValue('createdOn'),
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.lastUpdateBy'),
          value: getPersonValue('updatedBy'),
          bold: true,
        },
        {
          label: this.$t('landMeasurement.field.lastUpdate'),
          value: getItemValue('updatedOn'),
          bold: true,
        },
      ]

      return items
    },
  },
  methods: {
    handleModalShown() {
      this.totalDistance = null
      this.initMapPoly()

      if (this.item.geometry) {
        const regex = /\((-?\d+\.\d+),(-?\d+\.\d+)\)/g
        let match
        const resultArray = []

        while ((match = regex.exec(this.item.geometry)) !== null) {
          const lat = parseFloat(match[1])
          const lon = parseFloat(match[2])
          resultArray.push([lat, lon])
        }
        this.form.poly_data = resultArray
      }

      this.getSummaryTrip()
    },
    async initMapPoly() {
      const self = this
      await this.$refs.MapAddress.$mapPromise.then((map) => {
        self.map = map
      })

      let Coords = []
      this.form.poly_data.forEach(function (item) {
        Coords.push({
          lat: parseFloat(item[0]),
          lng: parseFloat(item[1]),
        })
      })

      // Construct the polygon.
      this.polygon = new this.google.maps.Polygon({
        paths: Coords,
        strokeColor: '#0D4491',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#1C74DA',
        fillOpacity: 0.35,
        editable: false,
        draggable: false,
      })

      // Set the polygon on the map
      this.polygon.setMap(this.map)

      // Fit the map to the bounds of the polygon
      const bounds = new this.google.maps.LatLngBounds()
      Coords.forEach((coord) => {
        bounds.extend(coord)
      })
      this.map.fitBounds(bounds)

      // Optionally, you can set a maximum zoom level to prevent zooming too close
      const maxZoom = 16
      const zoom = Math.min(maxZoom, this.map.getZoom())
      this.map.setZoom(zoom)

      // Add event listeners to update coordinates state
      this.polygon.getPaths().forEach((path) => {
        this.google.maps.event.addListener(path, 'insert_at', () => {
          this.updatePolygonCoordsState(path)
        })

        this.google.maps.event.addListener(path, 'remove_at', () => {
          this.updatePolygonCoordsState(path)
        })

        this.google.maps.event.addListener(path, 'set_at', () => {
          this.updatePolygonCoordsState(path)
        })
      })
    },
    updatePolygonCoordsState(poly) {
      this.form.poly_data = []

      const latLang = Array.from({ length: poly.getLength() }, (_, i) => {
        const koma = i === poly.getLength() - 1 ? '' : ','
        const lat = parseFloat(poly.getAt(i).lat()).toFixed(8)
        const lng = parseFloat(poly.getAt(i).lng()).toFixed(8)

        this.form.latitude += `${lat}${koma}`
        this.form.longitude += `${lng}${koma}`

        const coordinates = [lat, lng]
        this.form.poly_data.push(coordinates)

        return `(${lat}, ${lng})`
      })

      this.form.latLang = latLang.join(', ')
    },
    async getSummaryTrip() {
      this.totalDistance = null
      const filter = {
        vehicleId: this.item.vehicleId,
        startDate: moment(this.item.startTrip).toISOString(),
        endDate: moment(this.item.endTrip).toISOString(),
        pagination: {
          page: 1,
          show: 99999,
        },
        type: 'moving',
      }

      const res = await this.$apollo.query({
        query: GET_SUMMARY_TRIP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: filter,
        },
      })

      this.totalDistance = res?.data?.summaryTrips?.summaryTrips?.totalTrip ?? '-'
    },

    toDetail(zoneId) {
      this.$router.push({
        name: 'Land Measurement View',
        params: { id: zoneId },
      })
    },
  },
}
</script>
